// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SignInButtonStyles } from "@guestsupply/App/Components/Common/SignInButton";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import ProductAddToCartButton from "@guestsupply/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered, {
    ProductQuantityStyles,
} from "@guestsupply/Overrides/Components/ProductQuantityOrdered";
import { mergeWithCss } from "@guestsupply/XDL/Utilities/mergeWithCss";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Button } from "@insite/mobius/index";
import { TextFieldProps } from "@insite/mobius/TextField";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { FC, useState } from "react";

export interface ProductActionsProps {
    carouselId?: string;
    iconOnly?: boolean;
    showActions?: boolean;
    alwaysShowQty?: boolean;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
    showDisabled?: boolean;
    showAddTocart?: boolean;
    discardPrice?: boolean;
    // XNG-Change
    isPdp?: boolean;
}

type Props = ProductActionsProps & HasProductContext;

export interface ProductActionsStyle {
    addToCartContainer?: GridContainerProps;
    qtyWrapper?: GridItemProps;
    productQuantityOrderedStyles?: ProductQuantityStyles;
    addToCartButtonWrapper?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    signInButton?: SignInButtonStyles;
    productUomWrapper?: GridItemProps;
    viewOptionsButton?: ButtonPresentationProps;
}

export const productActionsStyle: ProductActionsStyle = {};

const ProductActions: FC<Props> = ({
    carouselId,
    extendedStyles,
    onAddToCartClick,
    iconOnly,
    productContext,
    showAddTocart = true,
    showDisabled,
    discardPrice = false,
    // XNG-Change
    isPdp = false,
}) => {
    const history = useHistory();
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    const {
        configurationCompleted,
        variantSelectionCompleted,
        configurationSelection,
        subscription,
        productSettings,
        isProductDetailsPage,
        isCurrentSelectedProductInPDP,
    } = useAppSelector(state => {
        const currentPage = getCurrentPage(state)?.type;
        const isProductDetailsPage = currentPage === "ProductDetailsPage";
        const currentSelectedProduct = state.pages.productDetails.selectedProductId;
        const isCurrentSelectedProductInPDP = currentSelectedProduct === productContext.product.id;
        return {
            configurationCompleted: isProductDetailsPage && state.pages.productDetails.configurationCompleted,
            variantSelectionCompleted: isProductDetailsPage && state.pages.productDetails.variantSelectionCompleted,
            configurationSelection: isProductDetailsPage
                ? state.pages.productDetails.configurationSelection
                : undefined,
            subscription: isProductDetailsPage ? state.pages.productDetails.subscription : undefined,
            productSettings: getSettingsCollection(state).productSettings,
            isProductDetailsPage,
            isCurrentSelectedProductInPDP,
        };
    });

    const handleViewOptionsClick = () => {
        history.push(productContext.productInfo.productDetailPath);
    };

    // XNG-CHANGES : showing add to cart in vareint product
    // if (productContext.product.isVariantParent && isProductDetailsPage && isCurrentSelectedProductInPDP) {
    //     return null;
    // }

    // // XNG-CHANGES : hiding View All Options in PDP
    if (productContext.product.isVariantParent && !isPdp) {
        return (
            <Button onClick={handleViewOptionsClick} {...styles.viewOptionsButton} aria-label="View All Options">
                {translate("View All Options")}
            </Button>
        );
    }

    if (productContext.product.isDiscontinued) {
        return null;
    }
    const inputId = `${uniqueId()}-qty-label`;
    const unitOfMeasures = productContext.product.unitOfMeasures;
    const willShowUnitOfMeasureSelect = !(
        !productSettings?.alternateUnitsOfMeasure ||
        !unitOfMeasures ||
        unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1
    );

    if (willShowUnitOfMeasureSelect) {
        return (
            <StyledWrapper {...styles.addToCartContainer} className="has-uom" data-test-selector="product_actions">
                <StyledWrapper {...styles.addToCartContainer}>
                    <StyledWrapper {...styles.qtyWrapper}>
                        <VisuallyHidden as="label" htmlFor={inputId}>
                            {translate("QTY")}
                        </VisuallyHidden>
                        <ProductQuantityOrdered
                            labelOverride=""
                            // XNG-CHANGES : showing disabled product quantity when varient product
                            showDisabled={showDisabled || (productContext.product.isVariantParent && isProductDetailsPage && isCurrentSelectedProductInPDP)}
                            extendedStyles={styles.productQuantityOrderedStyles}
                            data-test-selector="productQty"
                            id={inputId}
                            configurationCompleted={configurationCompleted}
                            variantSelectionCompleted={variantSelectionCompleted}
                            discardPrice={discardPrice}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productUomWrapper}>
                        {createWidgetElement("ProductDetails/ProductDetailsUnitOfMeasureSelect", {
                            fields: {},
                        })}
                    </StyledWrapper>
                </StyledWrapper>
                {showAddTocart && (
                    <StyledWrapper {...styles.addToCartButtonWrapper}>
                        <ProductAddToCartButton
                            {...{ iconOnly }}
                            // XNG-CHANGES : showing disabled add to cart when varient product
                            showDisabled={showDisabled || (productContext.product.isVariantParent && isProductDetailsPage && isCurrentSelectedProductInPDP)}
                            onAddToCartClick={onAddToCartClick}
                            extendedStyles={styles.productAddToCartButtonStyles}
                            data-test-selector="addToCartBtn"
                            configurationSelection={configurationSelection}
                            configurationCompleted={configurationCompleted}
                            variantSelectionCompleted={variantSelectionCompleted}
                            subscription={subscription}
                            discardPrice={discardPrice}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper {...styles.addToCartContainer} data-test-selector="product_actions">
            <StyledWrapper {...styles.qtyWrapper}>
                <VisuallyHidden as="label" htmlFor={inputId}>
                    {translate("QTY")}
                </VisuallyHidden>
                <ProductQuantityOrdered
                    labelOverride=""
                    showDisabled={showDisabled}
                    extendedStyles={styles.productQuantityOrderedStyles}
                    data-test-selector="productQty"
                    id={inputId}
                    configurationCompleted={configurationCompleted}
                    variantSelectionCompleted={variantSelectionCompleted}
                    discardPrice={discardPrice}
                />
            </StyledWrapper>
            {showAddTocart && (
                <StyledWrapper {...styles.addToCartButtonWrapper}>
                    <ProductAddToCartButton
                        {...{ iconOnly }}
                        showDisabled={showDisabled}
                        onAddToCartClick={onAddToCartClick}
                        extendedStyles={styles.productAddToCartButtonStyles}
                        data-test-selector="addToCartBtn"
                        configurationSelection={configurationSelection}
                        configurationCompleted={configurationCompleted}
                        variantSelectionCompleted={variantSelectionCompleted}
                        subscription={subscription}
                        discardPrice={discardPrice}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(ProductActions);
